import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    InputBase,
    IconButton,
    Grid,
    Menu,
    MenuItem,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import { useNavigate } from 'react-router-dom';
import UserApi from '../../../../rest-api/user-api';
import { useSnackbar } from '../../SnackbarProvider';
import DownloadUtils from '../../../../utils/download-utils';
import EditIcon from '../../../../assets/icons/CustomiseHandout.svg';
import ShareEnvelopIcon from '../../../../assets/icons/ShareEnvelop.svg';
import BinArchiveIcon from '../../../../assets/icons/BinArchive.svg';
import PinkGradientButtonWithArrow from '../../../UIControls/PinkGradientButtonWithArrow';
import PinkGradientButton from '../../../UIControls/PinkGradientButton';
import { archiveCustomisedHandout } from '../../../helper/customisedHandoutUtils';
import QuickShareDialog from '../quick-share-dialog';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';
import FavouriteButton from '../FavouriteButton';
import {
    getSavedFavouriteBaseContentIds,
    isSavedHandoutFavourite,
    toggleSavedHandoutFavourite,
} from '../../../../utils/saved-handout-favourites';
import { mergeProtectedImageStyle, protectedImageProps } from '../../../../utils/protected-image-props';

const location = 'en-GB';

const getVisiblePages = (currentPage, pageCount) => {
    if (pageCount <= 5) return Array.from({ length: pageCount }, (_, index) => index + 1);
    if (currentPage <= 3) return [1, 2, 3, 'ellipsis', pageCount];
    if (currentPage >= pageCount - 2) return [1, 'ellipsis', pageCount - 2, pageCount - 1, pageCount];
    return [1, 'ellipsis', currentPage, 'ellipsis', pageCount];
};

// Shows tooltip only when the text is actually truncated (overflows its container)
const TruncatedTooltip = ({ text, onClick }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    useEffect(() => {
        const el = textRef.current;
        if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [text]);
    return (
        <Tooltip title={text} placement="top" arrow disableHoverListener={!isTruncated}>
            <Typography
                ref={textRef}
                onClick={onClick}
                sx={{
                    fontSize: '13px',
                    lineHeight: '18px',
                    color: '#616063',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    width: '100%',
                }}
            >
                {text}
            </Typography>
        </Tooltip>
    );
};

const getCustomisedDocumentTitle = (item) => {
    const contentData = item.effectiveContentData?.['en-UK'];
    if (!contentData || !Array.isArray(contentData) || contentData.length === 0) return 'Untitled Handout';
    const firstSection = contentData[0];
    if (!firstSection?.blocks) return 'Untitled Handout';
    return firstSection.blocks.find((block) => block.type === 'documentTitle')?.value || 'Untitled Handout';
};

const getCustomisedFirstParagraph = (item) => {
    const contentData = item.effectiveContentData?.['en-UK'];
    if (!contentData || !Array.isArray(contentData)) return '';

    for (const section of contentData) {
        if (!section.blocks) continue;
        for (const block of section.blocks) {
            if ((block.type === 'paragraph' || block.type === 'text' || block.type === 'textBullet') && block.value) {
                return block.value.trim();
            }
        }
    }

    return '';
};

const getCustomisedImageSrcKeys = (item) => {
    const imageSrcKeys = [];

    if (item.attachedWhiteboardCanvasImageLink) {
        imageSrcKeys.push(item.attachedWhiteboardCanvasImageLink);
    }

    const contentData = item.effectiveContentData?.['en-UK'];
    if (!contentData || !Array.isArray(contentData)) return imageSrcKeys;

    for (const section of contentData) {
        if (!section.blocks) continue;
        section.blocks
            .filter((block) => block.type === 'illustration' && block.src)
            .forEach((block) => imageSrcKeys.push(block.src));
    }

    return [...new Set(imageSrcKeys)];
};

const MyLibrary = observer(() => {
    const navigate = useNavigate();
    const {
        userDataStore,
        authStore,
        imageStore,
        contentStore,
        customisedHandoutStore,
        savedHandoutStore,
        userCreatedHandoutStore,
    } = useContext(StoreContext);
    const showSnackbar = useSnackbar();

    const [customisedHandouts, setCustomisedHandouts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('RECENT');
    const [sortDirection, setSortDirection] = useState('desc');
    const [hasSortedRecently, setHasSortedRecently] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [itemsPerPage, setItemsPerPage] = useState(4);
    const [currentPage, setCurrentPage] = useState(1);
    const [savedSortBy, setSavedSortBy] = useState('RECENT');
    const [savedSortDirection, setSavedSortDirection] = useState('desc');
    const [hasSortedSavedRecently, setHasSortedSavedRecently] = useState(false);
    const [savedAnchorEl, setSavedAnchorEl] = useState(null);
    const [savedItemsPerPage, setSavedItemsPerPage] = useState(4);
    const [savedCurrentPage, setSavedCurrentPage] = useState(1);
    const [userCreatedItemsPerPage, setUserCreatedItemsPerPage] = useState(4);
    const [userCreatedCurrentPage, setUserCreatedCurrentPage] = useState(1);
    const [imageMap, setImageMap] = useState({});
    const [savedHandoutImageMap, setSavedHandoutImageMap] = useState({});
    const [userCreatedImageMap, setUserCreatedImageMap] = useState({});
    const [userCreatedStatusOverrides, setUserCreatedStatusOverrides] = useState({});
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [archiveTargetType, setArchiveTargetType] = useState('customised');
    const [noteTarget, setNoteTarget] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [quickShareOpen, setQuickShareOpen] = useState(false);
    const [quickShareHandout, setQuickShareHandout] = useState(null);
    const [isRemovingSavedFavourite, setIsRemovingSavedFavourite] = useState(false);

    const fetchedImages = useRef(new Set());
    const fetchedSavedImages = useRef(new Set());
    const fetchedUserCreatedImages = useRef(new Set());

    useEffect(() => {
        if (!customisedHandoutStore?.isInitialized) return;

        const myLibraryIds = userDataStore.userData.customisedHandouts || [];
        const data = Object.values(customisedHandoutStore.customisedHandoutData).filter(
            (ch) => ch.customisedStatus === 'active' && !ch.patientId && myLibraryIds.includes(ch.customisedId),
        );

        setCustomisedHandouts(data);
    }, [
        customisedHandoutStore?.customisedHandoutData,
        customisedHandoutStore?.isInitialized,
        userDataStore.userData.customisedHandouts,
    ]);

    const filteredAndSortedHandouts = useMemo(() => {
        const favouriteIds = userDataStore.userData.customisedHandoutsFavourites || [];

        const getDocumentTitle = (item) => {
            const contentData = item.effectiveContentData?.['en-UK'];
            if (!contentData || !Array.isArray(contentData) || contentData.length === 0) return '';
            const firstSection = contentData[0];
            if (!firstSection?.blocks) return '';
            return firstSection.blocks.find((block) => block.type === 'documentTitle')?.value || '';
        };

        const getTimestamp = (item) => {
            if (typeof item.lastUpdated === 'string') return new Date(item.lastUpdated).getTime();
            return item.lastUpdated || 0;
        };

        let items = [...customisedHandouts];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            items = items.filter((item) => {
                const title = getDocumentTitle(item).toLowerCase();
                const notes = (item.notes || '').toLowerCase();
                return title.includes(term) || notes.includes(term);
            });
        }

        if (sortBy === 'RECENT') {
            items.sort((a, b) =>
                sortDirection === 'asc' ? getTimestamp(a) - getTimestamp(b) : getTimestamp(b) - getTimestamp(a),
            );
        } else {
            const favourites = items
                .filter((item) => favouriteIds.includes(item.customisedId))
                .sort((a, b) =>
                    sortDirection === 'asc' ? getTimestamp(a) - getTimestamp(b) : getTimestamp(b) - getTimestamp(a),
                );
            const nonFavourites = items
                .filter((item) => !favouriteIds.includes(item.customisedId))
                .sort((a, b) =>
                    sortDirection === 'asc' ? getTimestamp(a) - getTimestamp(b) : getTimestamp(b) - getTimestamp(a),
                );
            items = [...favourites, ...nonFavourites];
        }

        return items;
    }, [customisedHandouts, searchTerm, sortBy, sortDirection, userDataStore.userData.customisedHandoutsFavourites]);

    const getUserCreatedFirstParagraph = useCallback((item) => {
        const contentData = item.contentData?.['en-UK'];
        if (!Array.isArray(contentData)) return '';
        for (const section of contentData) {
            const paragraph = (section.blocks || []).find((block) => block.type === 'paragraph' && block.value);
            if (paragraph?.value) return paragraph.value.trim();
        }
        return '';
    }, []);

    const getUserCreatedImageKey = useCallback((item) => {
        if (item?.selectedLibraryImage?.imageKey) return item.selectedLibraryImage.imageKey;
        const contentData = item.contentData?.['en-UK'];
        if (!Array.isArray(contentData)) return null;
        for (const section of contentData) {
            const illustration = (section.blocks || []).find((block) => block.type === 'illustration' && block.src);
            if (illustration?.src) return illustration.src;
        }
        return null;
    }, []);

    const userCreatedHandouts = useMemo(() => {
        const getTimestamp = (item) => {
            if (typeof item.lastUpdated === 'string') return new Date(item.lastUpdated).getTime();
            return item.lastUpdated || 0;
        };

        let items = Object.values(userCreatedHandoutStore?.userCreatedHandoutData || {});

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            items = items.filter((item) => {
                const title = (item.title || item.contentName || '').toLowerCase();
                const previewText = getUserCreatedFirstParagraph(item).toLowerCase();
                return title.includes(term) || previewText.includes(term);
            });
        }

        return items.sort((a, b) => getTimestamp(b) - getTimestamp(a));
    }, [getUserCreatedFirstParagraph, searchTerm, userCreatedHandoutStore?.userCreatedHandoutData]);

    const userCreatedPageCount = Math.max(1, Math.ceil(userCreatedHandouts.length / userCreatedItemsPerPage));
    const safeUserCreatedPage = Math.min(userCreatedCurrentPage, userCreatedPageCount);
    const visibleUserCreatedPages = getVisiblePages(safeUserCreatedPage, userCreatedPageCount);

    const paginatedUserCreatedHandouts = useMemo(() => {
        const start = (safeUserCreatedPage - 1) * userCreatedItemsPerPage;
        return userCreatedHandouts.slice(start, start + userCreatedItemsPerPage);
    }, [safeUserCreatedPage, userCreatedHandouts, userCreatedItemsPerPage]);

    useEffect(() => {
        let isMounted = true;

        const fetchUserCreatedImages = async () => {
            const newImageMap = {};
            let shouldUpdateState = false;

            for (const item of paginatedUserCreatedHandouts) {
                const imageKey = getUserCreatedImageKey(item);
                if (!imageKey || (fetchedUserCreatedImages.current.has(imageKey) && userCreatedImageMap[imageKey])) {
                    continue;
                }

                try {
                    const imageObject = await imageStore.getImageFromIndexedDB(imageKey);
                    if (imageObject?.data) {
                        newImageMap[imageKey] = imageObject.data;
                    } else {
                        const resp = await UserApi.generatePresignedUrls(authStore.idToken, {
                            files: [{ method: 'get', key: imageKey }],
                            contentCategory: 'image',
                        });
                        const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(
                            resp.getUrls[0].presignedUrl,
                        );
                        await imageStore.saveImageToIndexedDB(imageKey, downloadResult.base64data, downloadResult.eTag);
                        newImageMap[imageKey] = downloadResult.base64data;
                    }
                    fetchedUserCreatedImages.current.add(imageKey);
                    shouldUpdateState = true;
                } catch (error) {
                    fetchedUserCreatedImages.current.delete(imageKey);
                    console.error(`Error fetching user-created handout image: ${imageKey}`, error);
                }
            }

            if (isMounted && shouldUpdateState) {
                setUserCreatedImageMap((prev) => ({ ...prev, ...newImageMap }));
            }
        };

        if (paginatedUserCreatedHandouts.length > 0) fetchUserCreatedImages();

        return () => {
            isMounted = false;
        };
    }, [authStore.idToken, getUserCreatedImageKey, imageStore, paginatedUserCreatedHandouts, userCreatedImageMap]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortBy, sortDirection, itemsPerPage]);

    useEffect(() => {
        setSavedCurrentPage(1);
    }, [searchTerm, savedSortBy, savedSortDirection, savedItemsPerPage]);

    useEffect(() => {
        setUserCreatedCurrentPage(1);
    }, [searchTerm, userCreatedItemsPerPage]);

    const paginatedHandouts = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredAndSortedHandouts.slice(start, start + itemsPerPage);
    }, [filteredAndSortedHandouts, currentPage, itemsPerPage]);

    const isSearchingHandouts = searchTerm.trim().length > 0;
    const hasNoCustomisedHandouts = customisedHandouts.length === 0;

    useEffect(() => {
        let isMounted = true;

        const handleFetchHandoutIllustration = async (illustrationSrc) => {
            if (!illustrationSrc) return null;

            if (imageMap[illustrationSrc]) return imageMap[illustrationSrc];

            const imageObject = await imageStore.getImageFromIndexedDB(illustrationSrc);
            if (imageObject?.data) return imageObject.data;

            const isWhiteboardImage = illustrationSrc.startsWith('png/');
            const payload = {
                files: [{ method: 'get', key: illustrationSrc }],
                contentCategory: isWhiteboardImage ? 'whiteboard' : 'image',
            };

            const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
            const presignedUrl = resp.getUrls[0].presignedUrl;
            const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);

            await imageStore.saveImageToIndexedDB(illustrationSrc, downloadResult.base64data, downloadResult.eTag);
            return downloadResult.base64data;
        };

        const fetchImages = async () => {
            const newImageMap = {};
            let shouldUpdateState = false;

            for (const item of paginatedHandouts) {
                for (const imageSrc of getCustomisedImageSrcKeys(item)) {
                    if (!imageSrc || (fetchedImages.current.has(imageSrc) && imageMap[imageSrc])) continue;

                    try {
                        const imageData = await handleFetchHandoutIllustration(imageSrc);
                        if (isMounted && imageData) {
                            newImageMap[imageSrc] = imageData;
                            fetchedImages.current.add(imageSrc);
                            shouldUpdateState = true;
                        }
                    } catch (error) {
                        fetchedImages.current.delete(imageSrc);
                        console.error(`Error fetching customised handout image: ${imageSrc}`, error);
                    }
                }
            }

            if (isMounted && shouldUpdateState) {
                setImageMap((prev) => ({ ...prev, ...newImageMap }));
            }
        };

        if (paginatedHandouts.length > 0) fetchImages();

        return () => {
            isMounted = false;
        };
    }, [paginatedHandouts, imageMap, imageStore, authStore.idToken]);

    const savedFavouriteHandouts = useMemo(() => {
        const favouriteIds = userDataStore.userData.savedHandoutsFavourites || [];
        const savedHandoutData = savedHandoutStore?.savedHandoutData || {};
        const baseContentIds = getSavedFavouriteBaseContentIds(
            favouriteIds,
            savedHandoutData,
            contentStore?.contentData || {},
        );

        return baseContentIds
            .map((baseContentId) => {
                const baseHandout = contentStore?.contentData?.[baseContentId];
                if (!baseHandout) return null;

                const getTimestamp = (item) => {
                    if (typeof item?.lastUpdated === 'string') return new Date(item.lastUpdated).getTime();
                    return item?.lastUpdated || 0;
                };

                const legacySavedHandout = favouriteIds
                    .map((favouriteId) => savedHandoutData[favouriteId])
                    .find((item) => item?.baseContentId === baseContentId);
                const latestSavedHandout = Object.values(savedHandoutData)
                    .filter(
                        (item) =>
                            item?.baseContentId === baseContentId &&
                            (!item.savedStatus || item.savedStatus === 'active'),
                    )
                    .sort((a, b) => getTimestamp(b) - getTimestamp(a))[0];
                const savedHandout = legacySavedHandout || latestSavedHandout;

                return {
                    ...savedHandout,
                    favouriteType: 'original',
                    favouriteKey: `original-${baseContentId}`,
                    baseContentId,
                    handoutContentName: savedHandout?.handoutContentName || baseHandout.contentName,
                    handoutDocumentTitle: savedHandout?.handoutDocumentTitle || baseHandout.contentName,
                    lastUpdated: savedHandout?.lastUpdated || baseHandout.lastUpdated || baseHandout.createdAt || 0,
                    notes: savedHandout?.notes || '',
                };
            })
            .filter(Boolean);
    }, [
        contentStore?.contentData,
        savedHandoutStore?.savedHandoutData,
        userDataStore.userData.savedHandoutsFavourites,
    ]);

    const customisedFavouriteHandouts = useMemo(() => {
        const favouriteIds = userDataStore.userData.customisedHandoutsFavourites || [];
        const customisedHandoutData = customisedHandoutStore?.customisedHandoutData || {};
        const seen = new Set();

        return favouriteIds
            .map((customisedId) => {
                if (seen.has(customisedId)) return null;
                seen.add(customisedId);
                return customisedHandoutData[customisedId];
            })
            .filter((item) => item?.customisedId && (!item.customisedStatus || item.customisedStatus === 'active'))
            .map((item) => ({
                ...item,
                favouriteType: 'customised',
                favouriteKey: `customised-${item.customisedId}`,
            }));
    }, [customisedHandoutStore?.customisedHandoutData, userDataStore.userData.customisedHandoutsFavourites]);

    const getSavedBaseHandout = useCallback(
        (savedHandout) => {
            if (!savedHandout?.baseContentId) return null;
            return contentStore?.contentData?.[savedHandout.baseContentId] || null;
        },
        [contentStore],
    );

    const getSavedHandoutTitle = useCallback(
        (savedHandout) => {
            const baseHandout = getSavedBaseHandout(savedHandout);
            return (
                savedHandout.handoutDocumentTitle ||
                savedHandout.handoutContentName ||
                baseHandout?.contentName ||
                'Saved handout'
            );
        },
        [getSavedBaseHandout],
    );

    const getSavedHandoutFirstParagraph = useCallback(
        (savedHandout) => {
            const baseHandout = getSavedBaseHandout(savedHandout);
            const contentData = baseHandout?.contentData?.['en-UK'];
            if (!contentData || !Array.isArray(contentData)) return '';

            for (const section of contentData) {
                if (!section.blocks) continue;
                for (const block of section.blocks) {
                    if (
                        (block.type === 'paragraph' || block.type === 'text' || block.type === 'textBullet') &&
                        block.value
                    ) {
                        return block.value.trim();
                    }
                }
            }

            return '';
        },
        [getSavedBaseHandout],
    );

    const getSavedHandoutImageSrcKey = useCallback(
        (savedHandout) => {
            if (!savedHandout?.baseContentId) return null;
            const baseHandout = contentStore?.contentData?.[savedHandout.baseContentId] || null;
            const contentData = baseHandout?.contentData?.['en-UK'];
            if (!contentData || !Array.isArray(contentData)) return null;

            for (const section of contentData) {
                if (!section.blocks) continue;
                const illustrationBlock = section.blocks.find((block) => block.type === 'illustration');
                if (illustrationBlock?.src) return illustrationBlock.src;
            }

            return null;
        },
        [contentStore],
    );

    const filteredAndSortedSavedHandouts = useMemo(() => {
        const getTimestamp = (item) => {
            if (typeof item.lastUpdated === 'string') return new Date(item.lastUpdated).getTime();
            return item.lastUpdated || 0;
        };

        let items = [...savedFavouriteHandouts, ...customisedFavouriteHandouts];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            items = items.filter((item) => {
                const title =
                    item.favouriteType === 'customised'
                        ? getCustomisedDocumentTitle(item).toLowerCase()
                        : getSavedHandoutTitle(item).toLowerCase();
                const previewText =
                    item.favouriteType === 'customised'
                        ? getCustomisedFirstParagraph(item).toLowerCase()
                        : getSavedHandoutFirstParagraph(item).toLowerCase();
                const notes = (item.notes || '').toLowerCase();
                return title.includes(term) || previewText.includes(term) || notes.includes(term);
            });
        }

        items.sort((a, b) =>
            savedSortDirection === 'asc' ? getTimestamp(a) - getTimestamp(b) : getTimestamp(b) - getTimestamp(a),
        );

        return items;
    }, [
        savedFavouriteHandouts,
        customisedFavouriteHandouts,
        searchTerm,
        savedSortDirection,
        getSavedHandoutTitle,
        getSavedHandoutFirstParagraph,
    ]);

    const savedPageCount = Math.max(1, Math.ceil(filteredAndSortedSavedHandouts.length / savedItemsPerPage));
    const safeSavedPage = Math.min(savedCurrentPage, savedPageCount);
    const visibleSavedPages = getVisiblePages(safeSavedPage, savedPageCount);

    const paginatedSavedHandouts = useMemo(() => {
        const start = (safeSavedPage - 1) * savedItemsPerPage;
        return filteredAndSortedSavedHandouts.slice(start, start + savedItemsPerPage);
    }, [filteredAndSortedSavedHandouts, safeSavedPage, savedItemsPerPage]);

    useEffect(() => {
        let isMounted = true;

        const handleFetchSavedHandoutIllustration = async (illustrationSrc) => {
            if (!illustrationSrc) return null;
            if (savedHandoutImageMap[illustrationSrc]) return savedHandoutImageMap[illustrationSrc];

            const imageObject = await imageStore.getImageFromIndexedDB(illustrationSrc);
            if (imageObject?.data) return imageObject.data;

            const payload = {
                files: [{ method: 'get', key: illustrationSrc }],
                contentCategory: 'image',
            };

            const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
            const presignedUrl = resp.getUrls[0].presignedUrl;
            const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);

            await imageStore.saveImageToIndexedDB(illustrationSrc, downloadResult.base64data, downloadResult.eTag);
            return downloadResult.base64data;
        };

        const fetchSavedImages = async () => {
            const newImageMap = {};
            let shouldUpdateState = false;

            for (const item of paginatedSavedHandouts) {
                const imageSrc = getSavedHandoutImageSrcKey(item);
                if (!imageSrc || fetchedSavedImages.current.has(imageSrc)) continue;

                try {
                    const imageData = await handleFetchSavedHandoutIllustration(imageSrc);
                    if (isMounted && imageData) {
                        newImageMap[imageSrc] = imageData;
                        fetchedSavedImages.current.add(imageSrc);
                        shouldUpdateState = true;
                    }
                } catch (error) {
                    console.error(`Error fetching saved handout image: ${imageSrc}`, error);
                }
            }

            if (isMounted && shouldUpdateState) {
                setSavedHandoutImageMap((prev) => ({ ...prev, ...newImageMap }));
            }
        };

        if (paginatedSavedHandouts.length > 0) fetchSavedImages();

        return () => {
            isMounted = false;
        };
    }, [paginatedSavedHandouts, savedHandoutImageMap, getSavedHandoutImageSrcKey, imageStore, authStore.idToken]);

    useEffect(() => {
        let isMounted = true;

        const handleFetchCustomisedFavouriteIllustration = async (illustrationSrc) => {
            if (!illustrationSrc) return null;
            if (imageMap[illustrationSrc]) return imageMap[illustrationSrc];

            const imageObject = await imageStore.getImageFromIndexedDB(illustrationSrc);
            if (imageObject?.data) return imageObject.data;

            const isWhiteboardImage = illustrationSrc.startsWith('png/');
            const payload = {
                files: [{ method: 'get', key: illustrationSrc }],
                contentCategory: isWhiteboardImage ? 'whiteboard' : 'image',
            };

            const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
            const presignedUrl = resp.getUrls[0].presignedUrl;
            const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);

            await imageStore.saveImageToIndexedDB(illustrationSrc, downloadResult.base64data, downloadResult.eTag);
            return downloadResult.base64data;
        };

        const fetchCustomisedFavouriteImages = async () => {
            const newImageMap = {};
            let shouldUpdateState = false;
            const customisedFavouritePageItems = paginatedSavedHandouts.filter(
                (item) => item.favouriteType === 'customised',
            );

            for (const item of customisedFavouritePageItems) {
                for (const imageSrc of getCustomisedImageSrcKeys(item)) {
                    if (!imageSrc || (fetchedImages.current.has(imageSrc) && imageMap[imageSrc])) continue;

                    try {
                        const imageData = await handleFetchCustomisedFavouriteIllustration(imageSrc);
                        if (isMounted && imageData) {
                            newImageMap[imageSrc] = imageData;
                            fetchedImages.current.add(imageSrc);
                            shouldUpdateState = true;
                        }
                    } catch (error) {
                        fetchedImages.current.delete(imageSrc);
                        console.error(`Error fetching customised favourite handout image: ${imageSrc}`, error);
                    }
                }
            }

            if (isMounted && shouldUpdateState) {
                setImageMap((prev) => ({ ...prev, ...newImageMap }));
            }
        };

        if (paginatedSavedHandouts.some((item) => item.favouriteType === 'customised')) {
            fetchCustomisedFavouriteImages();
        }

        return () => {
            isMounted = false;
        };
    }, [paginatedSavedHandouts, imageMap, imageStore, authStore.idToken]);

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleOpenSavedMenu = (event) => {
        setSavedAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = (option) => {
        if (option === 'RECENT') {
            setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
            setHasSortedRecently(true);
            setSortBy('RECENT');
        }

        if (option === 'FAVOURITES') {
            const isSameSort = sortBy === 'FAVOURITES';
            setSortDirection((prev) => (isSameSort && prev === 'desc' ? 'asc' : 'desc'));
            setHasSortedRecently(false);
            setSortBy('FAVOURITES');
        }

        setAnchorEl(null);
    };

    const handleCloseSavedMenu = (option) => {
        if (option === 'RECENT') {
            setSavedSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
            setHasSortedSavedRecently(true);
            setSavedSortBy('RECENT');
        }

        if (option === 'FAVOURITES') {
            const isSameSort = savedSortBy === 'FAVOURITES';
            setSavedSortDirection((prev) => (isSameSort && prev === 'desc' ? 'asc' : 'desc'));
            setHasSortedSavedRecently(false);
            setSavedSortBy('FAVOURITES');
        }

        setSavedAnchorEl(null);
    };

    const handleToggleFavouriteCustomisedHandout = async (customisedId) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const isFavourite = userDataStore.userData.customisedHandoutsFavourites?.includes(customisedId);
        const updatedFavourites = isFavourite
            ? userDataStore.userData.customisedHandoutsFavourites.filter((id) => id !== customisedId)
            : [...(userDataStore.userData.customisedHandoutsFavourites || []), customisedId];

        if (!isFavourite && (userDataStore.userData.customisedHandoutsFavourites?.length || 0) >= 30) {
            showSnackbar('You can only mark up to 30 customised handouts as favourites.', 'warning');
            return;
        }

        userDataStore.setUserData({
            ...userDataStore.userData,
            customisedHandoutsFavourites: updatedFavourites,
        });

        const payload = { customisedHandoutsFavourites: updatedFavourites };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
        } catch (err) {
            console.error('Failed to update customised handout favourites', err);
        }
    };

    const handleToggleFavouriteSavedHandout = async (baseContentId) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const currentFavourites = userDataStore.userData.savedHandoutsFavourites || [];
        const { updatedFavourites, limitReached } = toggleSavedHandoutFavourite(
            baseContentId,
            currentFavourites,
            savedHandoutStore?.savedHandoutData || {},
        );

        if (limitReached) {
            showSnackbar('You can only mark up to 30 saved handouts as favourites.', 'warning');
            return;
        }

        userDataStore.setUserData({
            ...userDataStore.userData,
            savedHandoutsFavourites: updatedFavourites,
        });

        const payload = { savedHandoutsFavourites: updatedFavourites };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
        } catch (err) {
            console.error('Failed to update saved handout favourites', err);
        }
    };

    const handleEditCustomisedHandout = (customisedHandout) => {
        if (!customisedHandout.baseContentId) {
            showSnackbar('Cannot edit: missing base content ID', 'error');
            return;
        }

        localStorage.removeItem('lastWhiteboardState');
        localStorage.setItem('loadCustomisedHandout', customisedHandout.customisedId);
        localStorage.setItem('loadCustomisedHandoutInEditMode', 'true');
        navigate(`/handout/${customisedHandout.baseContentId}`);
    };

    const handleOpenCustomisedHandoutView = (customisedHandout) => {
        if (!customisedHandout.baseContentId) {
            showSnackbar('Cannot open: missing base content ID', 'error');
            return;
        }

        localStorage.removeItem('lastWhiteboardState');
        localStorage.removeItem('loadCustomisedHandoutInEditMode');
        localStorage.setItem('loadCustomisedHandout', customisedHandout.customisedId);
        navigate(`/handout/${customisedHandout.baseContentId}`);
    };

    const handleQuickShare = (customisedHandout) => {
        const handoutForShare = {
            ...customisedHandout,
            contentId: customisedHandout.baseContentId,
            contentName: getDocumentTitle(customisedHandout),
        };
        setQuickShareHandout(handoutForShare);
        setQuickShareOpen(true);
    };

    const handleQuickShareSavedHandout = (savedHandout) => {
        const baseHandout = getSavedBaseHandout(savedHandout);
        if (!baseHandout?.contentId) {
            showSnackbar('Cannot share: missing base handout.', 'error');
            return;
        }
        setQuickShareHandout(baseHandout);
        setQuickShareOpen(true);
    };

    const handleQuickShareUserCreatedHandout = (handout) => {
        if (handout.contentStatus !== 'published') {
            showSnackbar('Publish this handout before sharing.', 'warning');
            return;
        }

        setQuickShareHandout({
            ...handout,
            contentId: handout.handoutId || handout.contentId,
            userCreatedHandoutId: handout.handoutId || handout.contentId,
            contentType: 'userCreatedHandout',
        });
        setQuickShareOpen(true);
    };

    const handleToggleUserCreatedStatus = async (handout) => {
        const nextStatus = handout.contentStatus === 'published' ? 'unpublished' : 'published';
        const id = handout.handoutId || handout.contentId;
        setUserCreatedStatusOverrides((prev) => ({ ...prev, [id]: nextStatus }));
        try {
            const response = await UserApi.createOrUpdateUserCreatedHandout(authStore.idToken, {
                ...handout,
                contentStatus: nextStatus,
            });
            const updatedHandout = {
                ...handout,
                ...(response.handout || {}),
                contentStatus: nextStatus,
            };
            await userCreatedHandoutStore.addOrUpdateUserCreatedHandout(updatedHandout);
            showSnackbar(nextStatus === 'published' ? 'Handout published' : 'Handout unpublished', 'success');
        } catch (error) {
            setUserCreatedStatusOverrides((prev) => ({ ...prev, [id]: handout.contentStatus }));
            console.error('Failed to update user-created handout status', error);
            showSnackbar('Failed to update handout status', 'error');
        }
    };

    const handleArchiveCustomisedHandout = async () => {
        if (!archiveTarget) return;

        await archiveCustomisedHandout(
            archiveTarget,
            userDataStore,
            customisedHandoutStore,
            authStore,
            setCustomisedHandouts,
            () => {},
            showSnackbar,
        );
        setArchiveTarget(null);
    };

    const handleRemoveFavourite = async () => {
        if (!archiveTarget) return;

        setIsRemovingSavedFavourite(true);
        try {
            const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
            const payload = {};
            const nextUserData = { ...userDataStore.userData };

            if (archiveTargetType === 'customisedFavourite') {
                const updatedCustomisedFavourites = (userDataStore.userData.customisedHandoutsFavourites || []).filter(
                    (id) => id !== archiveTarget.customisedId,
                );
                payload.customisedHandoutsFavourites = updatedCustomisedFavourites;
                nextUserData.customisedHandoutsFavourites = updatedCustomisedFavourites;
            } else {
                const existingSavedFavourites = userDataStore.userData.savedHandoutsFavourites || [];
                const { updatedFavourites: updatedSavedFavourites } = toggleSavedHandoutFavourite(
                    archiveTarget.baseContentId,
                    existingSavedFavourites,
                    savedHandoutStore?.savedHandoutData || {},
                );
                payload.savedHandoutsFavourites = updatedSavedFavourites;
                nextUserData.savedHandoutsFavourites = updatedSavedFavourites;
            }

            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }

            userDataStore.setUserData(nextUserData);

            showSnackbar('Handout removed from favourites', 'success');
        } catch (err) {
            console.error('Failed to remove saved handout favourite', err);
            showSnackbar('Failed to remove handout from favourites', 'error');
        } finally {
            setIsRemovingSavedFavourite(false);
            setArchiveTarget(null);
        }
    };

    const handleSaveNote = async () => {
        if (!noteTarget) return;
        try {
            await UserApi.createOrUpdateCustomisedHandout(authStore.idToken, {
                customisedId: noteTarget.customisedId,
                baseContentId: noteTarget.baseContentId,
                notes: noteText,
            });
            const updatedHandout = { ...noteTarget, notes: noteText };
            delete updatedHandout.favouriteType;
            delete updatedHandout.favouriteKey;
            await customisedHandoutStore.addOrUpdateCustomisedHandout(updatedHandout);
            setCustomisedHandouts((prev) =>
                prev.map((h) => (h.customisedId === noteTarget.customisedId ? updatedHandout : h)),
            );
            setNoteTarget(null);
            setNoteText('');
            showSnackbar('Note saved', 'success');
        } catch (err) {
            console.error('Error saving note:', err);
            showSnackbar('Failed to save note', 'error');
        }
    };

    const getDocumentTitle = (item) => {
        const contentData = item.effectiveContentData?.['en-UK'];
        if (!contentData || !Array.isArray(contentData) || contentData.length === 0) return 'Untitled Handout';
        const firstSection = contentData[0];
        if (!firstSection?.blocks) return 'Untitled Handout';
        return firstSection.blocks.find((block) => block.type === 'documentTitle')?.value || 'Untitled Handout';
    };

    const getFirstParagraph = (item) => {
        const contentData = item.effectiveContentData?.['en-UK'];
        if (!contentData || !Array.isArray(contentData)) return '';

        for (const section of contentData) {
            if (!section.blocks) continue;
            for (const block of section.blocks) {
                if (
                    (block.type === 'paragraph' || block.type === 'text' || block.type === 'textBullet') &&
                    block.value
                ) {
                    return block.value.trim();
                }
            }
        }

        return '';
    };

    const getImageSource = (item) => {
        for (const imageSrc of getCustomisedImageSrcKeys(item)) {
            if (imageMap[imageSrc]) return imageMap[imageSrc];
        }
        return null;
    };

    const pageCount = Math.max(1, Math.ceil(filteredAndSortedHandouts.length / itemsPerPage));
    const safePage = Math.min(currentPage, pageCount);
    const visiblePages = getVisiblePages(safePage, pageCount);
    const isLoadingUserCreatedHandouts = !userCreatedHandoutStore || !userCreatedHandoutStore.isInitialized;

    return (
        <Box
            sx={{
                px: { xs: '16px', md: '20px' },
                pb: { xs: '24px', md: '24px' },
                background: '#F0F0FC',
                minHeight: 'calc(100vh - 140px)',
            }}
        >
            {isRemovingSavedFavourite && <BlockingSpinner open={true} />}
            <Box
                sx={{
                    maxWidth: '1360px',
                    margin: '0 auto',
                    pt: { xs: '20px', md: '34px' },
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '16px',
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        mb: '24px',
                    }}
                >
                    <Typography
                        sx={{
                            color: '#000453',
                            fontSize: { xs: '24px', md: '40px' },
                            lineHeight: { xs: '24px', md: '40px' },
                            fontWeight: 600,
                            letterSpacing: { xs: '-0.96px', md: '-1.6px' },
                        }}
                    >
                        My handouts
                    </Typography>
                    <Box
                        sx={{
                            borderRadius: '89.324px',
                            background: 'linear-gradient(176.249deg, #FF669B 0%, #BD8DE8 110.58%)',
                            height: '50px',
                            width: { xs: '100%', md: '251px' },
                            display: 'flex',
                            alignItems: 'center',
                            px: '16px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <SearchIcon sx={{ color: '#FFFFFF', opacity: 0.8, fontSize: 20, mr: 1, flexShrink: 0 }} />
                        <InputBase
                            type="text"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="Search handouts..."
                            fullWidth
                            inputProps={{ spellCheck: false }}
                            sx={{
                                width: '100%',
                                height: '100%',
                                background: 'transparent',
                                color: 'white',
                                '& .MuiInputBase-input': {
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    appearance: 'none',
                                    color: 'white',
                                    fontSize: '16px',
                                    lineHeight: '16px',
                                    letterSpacing: '-0.32px',
                                    fontWeight: 400,
                                    padding: 0,
                                    border: 0,
                                    borderRadius: 0,
                                    outline: 0,
                                    margin: 0,
                                    backgroundColor: 'transparent !important',
                                    boxShadow: 'none',
                                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                                    WebkitTextFillColor: 'white',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(255,255,255,0.95)',
                                    opacity: 1,
                                },
                                '& input:-webkit-autofill': {
                                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                                    WebkitTextFillColor: 'white !important',
                                    transition: 'background-color 9999s ease-out 0s',
                                },
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        order: 30,
                        width: '100%',
                        maxWidth: '1361px',
                        background: '#FFFFFF',
                        borderRadius: '30px',
                        border: '1px solid #ECECFA',
                        p: { xs: '14px', md: '20px' },
                        mt: { xs: '16px', md: '24px' },
                        mb: { xs: '16px', md: '24px' },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', md: 'center' },
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: '12px',
                            mb: '18px',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: { xs: '20px', md: '30px' },
                                lineHeight: { xs: '20px', md: '30px' },
                                fontWeight: 600,
                                color: '#000453',
                                letterSpacing: { xs: '-0.8px', md: '-1.2px' },
                            }}
                        >
                            My custom built handouts
                        </Typography>
                        <PinkGradientButtonWithArrow
                            label="Build a handout"
                            onClick={() => navigate('/build-handout')}
                            buttonStyle="figmaCta"
                        />
                    </Box>

                    {isLoadingUserCreatedHandouts ? (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <CircularProgress size={28} sx={{ color: '#FF669B' }} />
                            <Typography variant="h6" sx={{ color: '#000453', mt: 1 }}>
                                Loading your handouts...
                            </Typography>
                        </Box>
                    ) : userCreatedHandouts.length === 0 ? (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ color: '#000453' }}>
                                {isSearchingHandouts
                                    ? 'No handouts you created match your search.'
                                    : 'No handouts created yet.'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2}>
                                {paginatedUserCreatedHandouts.map((item) => {
                                    const imageKey = getUserCreatedImageKey(item);
                                    const imageSrc = imageKey ? userCreatedImageMap[imageKey] : null;
                                    const title = item.title || item.contentName || 'Untitled handout';
                                    const itemId = item.handoutId || item.contentId;
                                    const effectiveStatus = userCreatedStatusOverrides[itemId] || item.contentStatus;
                                    const effectiveItem = { ...item, contentStatus: effectiveStatus };
                                    const isPublished = effectiveStatus === 'published';

                                    return (
                                        <Grid item xs={12} sm={6} md={3} key={itemId}>
                                            <Box
                                                onClick={() => navigate(`/my-handout/${itemId}`)}
                                                sx={{
                                                    background: '#F0F0FC',
                                                    borderRadius: '20px',
                                                    p: '10px',
                                                    border: '1px solid #ECECFA',
                                                    width: '100%',
                                                    maxWidth: { xs: '100%', md: '314px' },
                                                    mx: { xs: 0, md: 'auto' },
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        background: '#FFFFFF',
                                                        borderRadius: '10px',
                                                        p: '8px',
                                                        minHeight: '248px',
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                                        <Tooltip title={isPublished ? 'Published' : 'Unpublished'} arrow>
                                                            <Box
                                                                sx={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    borderRadius: '50%',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: '#FF669B',
                                                                }}
                                                            >
                                                                {isPublished ? (
                                                                    <VisibilityIcon sx={{ fontSize: 20 }} />
                                                                ) : (
                                                                    <VisibilityOffIcon sx={{ fontSize: 20 }} />
                                                                )}
                                                            </Box>
                                                        </Tooltip>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: '168px',
                                                            borderRadius: '10px',
                                                            overflow: 'hidden',
                                                            background: '#FFFFFF',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        {imageSrc ? (
                                                            <img
                                                                src={imageSrc}
                                                                alt={title}
                                                                {...protectedImageProps}
                                                                style={mergeProtectedImageStyle({
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain',
                                                                })}
                                                            />
                                                        ) : (
                                                            <Typography sx={{ color: '#A1A1B3', fontSize: '12px' }}>
                                                                {imageKey ? 'No preview' : 'No image'}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Typography sx={{ mt: 1, color: '#A1A1B3', fontSize: '12px' }}>
                                                        Updated on:{' '}
                                                        {new Date(item.lastUpdated || Date.now()).toLocaleString(
                                                            location,
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        color: '#000453',
                                                        fontWeight: 600,
                                                        fontSize: '22px',
                                                        lineHeight: '24px',
                                                        mt: '10px',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        minHeight: '48px',
                                                    }}
                                                >
                                                    {title}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: '#616063',
                                                        fontSize: '14px',
                                                        lineHeight: '18px',
                                                        mt: '6px',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        minHeight: '36px',
                                                    }}
                                                >
                                                    {getUserCreatedFirstParagraph(item)}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        mt: '10px',
                                                        borderTop: '1px solid #D8D8EA',
                                                        pt: '10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <Tooltip title={isPublished ? 'Quick share' : 'Publish before sharing'} arrow>
                                                        <Box
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                if (!isPublished) return;
                                                                handleQuickShareUserCreatedHandout(effectiveItem);
                                                            }}
                                                            sx={{
                                                                width: '50px',
                                                                height: '50px',
                                                                borderRadius: '50%',
                                                                background: '#FF669B',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: isPublished ? 'pointer' : 'default',
                                                                opacity: isPublished ? 1 : 0.45,
                                                            }}
                                                        >
                                                            <img
                                                                src={ShareEnvelopIcon}
                                                                alt="Share"
                                                                width={22}
                                                                height={22}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <Tooltip title="Edit" arrow>
                                                            <Box
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    navigate(`/build-handout/${itemId}`);
                                                                }}
                                                                sx={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    background: '#FF669B',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                <img src={EditIcon} alt="Edit" width={22} height={22} />
                                                            </Box>
                                                        </Tooltip>
                                                        <Tooltip title={isPublished ? 'Unpublish' : 'Publish'} arrow>
                                                            <Box
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handleToggleUserCreatedStatus(effectiveItem);
                                                                }}
                                                                sx={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    background: '#FF669B',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    color: '#FFFFFF',
                                                                }}
                                                            >
                                                                {isPublished ? (
                                                                    <VisibilityOffIcon sx={{ fontSize: 24 }} />
                                                                ) : (
                                                                    <PublishIcon sx={{ fontSize: 24 }} />
                                                                )}
                                                            </Box>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Box
                                sx={{
                                    mt: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <IconButton
                                        size="small"
                                        disabled={safeUserCreatedPage <= 1}
                                        onClick={() => setUserCreatedCurrentPage((value) => Math.max(1, value - 1))}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '32px',
                                            border: '1.313px solid #E5E5E5',
                                            background: '#FFFFFF',
                                            color: '#6D6D6D',
                                            '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                        }}
                                    >
                                        <ChevronLeftIcon sx={{ fontSize: '15px' }} />
                                    </IconButton>
                                    {visibleUserCreatedPages.map((pageItem, index) => {
                                        if (pageItem === 'ellipsis') {
                                            return (
                                                <Box
                                                    key={`user-created-ellipsis-${index}`}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '8px',
                                                        background: '#FFFFFF',
                                                        color: '#616063',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '16px',
                                                    }}
                                                >
                                                    ...
                                                </Box>
                                            );
                                        }

                                        const isActive = pageItem === safeUserCreatedPage;
                                        return (
                                            <Box
                                                key={`user-created-${pageItem}`}
                                                onClick={() => setUserCreatedCurrentPage(pageItem)}
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '32px',
                                                    border: isActive ? 'none' : '1.313px solid #E5E5E5',
                                                    background: isActive ? '#FF669B' : '#FFFFFF',
                                                    color: isActive ? '#FFFFFF' : '#616063',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '13px',
                                                    fontWeight: isActive ? 400 : 500,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {pageItem}
                                            </Box>
                                        );
                                    })}
                                    <IconButton
                                        size="small"
                                        disabled={safeUserCreatedPage >= userCreatedPageCount}
                                        onClick={() =>
                                            setUserCreatedCurrentPage((value) =>
                                                Math.min(userCreatedPageCount, value + 1),
                                            )
                                        }
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '32px',
                                            border: '1.313px solid #E5E5E5',
                                            background: '#FFFFFF',
                                            color: '#6D6D6D',
                                            '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                        }}
                                    >
                                        <ChevronRightIcon sx={{ fontSize: '15px' }} />
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Typography
                                        sx={{
                                            color: '#616063',
                                            fontSize: { xs: '14px', md: '13px' },
                                            lineHeight: { xs: '14px', md: '13px' },
                                        }}
                                    >
                                        Items per page
                                    </Typography>
                                    <Select
                                        size="small"
                                        value={userCreatedItemsPerPage}
                                        onChange={(event) => setUserCreatedItemsPerPage(Number(event.target.value))}
                                        sx={{
                                            width: { xs: 63, md: 'auto' },
                                            height: 30,
                                            borderRadius: '10px',
                                            fontSize: '13px',
                                            color: '#616063',
                                            '& .MuiSelect-select': { py: 0, minHeight: 'unset', lineHeight: '13px' },
                                        }}
                                    >
                                        {[4, 8, 12].map((value) => (
                                            <MenuItem key={value} value={value}>
                                                {value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '1361px',
                        background: '#FFFFFF',
                        borderRadius: '30px',
                        border: '1px solid #ECECFA',
                        p: { xs: '14px', md: '20px' },
                        mb: { xs: '16px', md: '24px' },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: { xs: 'flex-start', md: 'space-between' },
                            alignItems: { xs: 'flex-start', md: 'center' },
                            mb: '18px',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: { xs: '20px', md: '30px' },
                                lineHeight: { xs: '20px', md: '30px' },
                                fontWeight: 600,
                                color: '#000453',
                                letterSpacing: { xs: '-0.8px', md: '-1.2px' },
                            }}
                        >
                            My favourites
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: '6px', md: 0 } }}>
                            <Typography
                                onClick={handleOpenSavedMenu}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: { xs: '12px', md: '14px' },
                                    lineHeight: { xs: '12px', md: '14px' },
                                    letterSpacing: { xs: '1.826px', md: '2px' },
                                    textTransform: 'uppercase',
                                    color: '#1919194D',
                                    cursor: 'pointer',
                                }}
                            >
                                SORT BY{' '}
                                <Box component="span" sx={{ color: '#000453' }}>
                                    {savedSortBy}
                                </Box>
                            </Typography>
                            <IconButton onClick={handleOpenSavedMenu} sx={{ ml: 0.5, p: 0 }}>
                                {savedSortBy === 'RECENT' && hasSortedSavedRecently ? (
                                    savedSortDirection === 'desc' ? (
                                        <ExpandMoreIcon
                                            sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }}
                                        />
                                    ) : (
                                        <ExpandLessIcon
                                            sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }}
                                        />
                                    )
                                ) : (
                                    <ExpandMoreIcon sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }} />
                                )}
                            </IconButton>
                        </Box>
                    </Box>

                    <Menu
                        anchorEl={savedAnchorEl}
                        open={Boolean(savedAnchorEl)}
                        onClose={() => handleCloseSavedMenu(null)}
                    >
                        <MenuItem onClick={() => handleCloseSavedMenu('RECENT')} sx={{ color: '#000453' }}>
                            RECENT
                        </MenuItem>
                        <MenuItem onClick={() => handleCloseSavedMenu('FAVOURITES')} sx={{ color: '#000453' }}>
                            FAVOURITES
                        </MenuItem>
                    </Menu>

                    {filteredAndSortedSavedHandouts.length === 0 ? (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ color: '#000453' }}>
                                {isSearchingHandouts
                                    ? 'No favourite handouts match your search.'
                                    : 'No favourite handouts available yet.'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2}>
                                {paginatedSavedHandouts.map((item) => {
                                    const isCustomisedFavourite = item.favouriteType === 'customised';
                                    const isFavourite = isCustomisedFavourite
                                        ? userDataStore.userData.customisedHandoutsFavourites?.includes(
                                              item.customisedId,
                                          )
                                        : isSavedHandoutFavourite(
                                              item.baseContentId,
                                              userDataStore.userData.savedHandoutsFavourites || [],
                                              savedHandoutStore?.savedHandoutData || {},
                                          );
                                    const title = isCustomisedFavourite
                                        ? getCustomisedDocumentTitle(item)
                                        : getSavedHandoutTitle(item);
                                    const previewText = isCustomisedFavourite
                                        ? getCustomisedFirstParagraph(item)
                                        : getSavedHandoutFirstParagraph(item);
                                    const imageSrc = isCustomisedFavourite ? getImageSource(item) : null;

                                    return (
                                        <Grid item xs={12} sm={6} md={3} key={item.favouriteKey}>
                                            <Box
                                                onClick={() => {
                                                    if (isCustomisedFavourite) {
                                                        handleOpenCustomisedHandoutView(item);
                                                    } else {
                                                        localStorage.removeItem('loadSavedHandout');
                                                        navigate(`/handout/${item.baseContentId}`);
                                                    }
                                                }}
                                                sx={{
                                                    background: '#F0F0FC',
                                                    borderRadius: '20px',
                                                    p: '10px',
                                                    border: '1px solid #ECECFA',
                                                    width: '100%',
                                                    maxWidth: { xs: '100%', md: '314px' },
                                                    mx: { xs: 0, md: 'auto' },
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        background: '#FFFFFF',
                                                        borderRadius: '10px',
                                                        p: '8px',
                                                        minHeight: '248px',
                                                    }}
                                                >
                                                    <FavouriteButton
                                                        isFavourite={isFavourite}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (isCustomisedFavourite) {
                                                                handleToggleFavouriteCustomisedHandout(
                                                                    item.customisedId,
                                                                );
                                                            } else {
                                                                handleToggleFavouriteSavedHandout(item.baseContentId);
                                                            }
                                                        }}
                                                        size={32}
                                                        iconSize={32}
                                                        tooltipTitle={
                                                            isFavourite ? 'Remove from favourites' : 'Add to favourites'
                                                        }
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '10px',
                                                            right: '10px',
                                                            zIndex: 2,
                                                        }}
                                                    />
                                                    {isCustomisedFavourite && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: '11px',
                                                                lineHeight: '14px',
                                                                color: '#A1A1B3',
                                                                pr: '36px',
                                                                mb: 1,
                                                            }}
                                                        >
                                                            Updated on:{' '}
                                                            {new Date(item.lastUpdated).toLocaleString(location)}
                                                        </Typography>
                                                    )}

                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: '168px',
                                                            borderRadius: '10px',
                                                            overflow: 'hidden',
                                                            background: '#FFFFFF',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        {(() => {
                                                            const imageKey = getSavedHandoutImageSrcKey(item);
                                                            const originalImageSrc = imageKey
                                                                ? savedHandoutImageMap[imageKey]
                                                                : null;
                                                            const resolvedImageSrc = isCustomisedFavourite
                                                                ? imageSrc
                                                                : originalImageSrc;
                                                            return resolvedImageSrc ? (
                                                                <img
                                                                    src={resolvedImageSrc}
                                                                    alt={title}
                                                                    {...protectedImageProps}
                                                                    style={mergeProtectedImageStyle({
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'contain',
                                                                    })}
                                                                />
                                                            ) : (
                                                                <Typography sx={{ color: '#A1A1B3', fontSize: '12px' }}>
                                                                    No preview
                                                                </Typography>
                                                            );
                                                        })()}
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            mt: '8px',
                                                            minHeight: '28px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {item.notes ? (
                                                            <TruncatedTooltip
                                                                text={item.notes}
                                                                onClick={(e) => {
                                                                    if (!isCustomisedFavourite) return;
                                                                    e.stopPropagation();
                                                                    setNoteTarget(item);
                                                                    setNoteText(item.notes);
                                                                }}
                                                            />
                                                        ) : isCustomisedFavourite ? (
                                                            <Box
                                                                component="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setNoteTarget(item);
                                                                    setNoteText('');
                                                                }}
                                                                sx={{
                                                                    border: '1.5px solid #FF669B',
                                                                    borderRadius: '999px',
                                                                    px: '12px',
                                                                    py: '4px',
                                                                    color: '#FF669B',
                                                                    fontSize: '13px',
                                                                    fontWeight: 500,
                                                                    background: 'transparent',
                                                                    cursor: 'pointer',
                                                                    fontFamily: 'Inter, sans-serif',
                                                                    '&:hover': { background: 'rgba(255,102,155,0.08)' },
                                                                }}
                                                            >
                                                                Add note
                                                            </Box>
                                                        ) : null}
                                                    </Box>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        color: '#000453',
                                                        fontWeight: 600,
                                                        fontSize: '22px',
                                                        lineHeight: '24px',
                                                        letterSpacing: '-0.36px',
                                                        mt: '10px',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        minHeight: '48px',
                                                    }}
                                                >
                                                    {title}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: '#616063',
                                                        fontSize: '14px',
                                                        lineHeight: '18px',
                                                        mt: '6px',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        minHeight: '36px',
                                                    }}
                                                >
                                                    {previewText}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        mt: '10px',
                                                        borderTop: '1px solid #D8D8EA',
                                                        pt: '10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <Tooltip title="Quick share" arrow>
                                                        <Box
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isCustomisedFavourite) {
                                                                    handleQuickShare(item);
                                                                } else {
                                                                    handleQuickShareSavedHandout(item);
                                                                }
                                                            }}
                                                            sx={{
                                                                width: '50px',
                                                                height: '50px',
                                                                borderRadius: '50%',
                                                                background: '#FF669B',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                '&:hover': { background: '#FF85B0' },
                                                            }}
                                                        >
                                                            <img
                                                                src={ShareEnvelopIcon}
                                                                alt="Share"
                                                                width={22}
                                                                height={22}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <Tooltip title="Open in customise mode" arrow>
                                                            <Box
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (isCustomisedFavourite) {
                                                                        handleEditCustomisedHandout(item);
                                                                    } else {
                                                                        localStorage.setItem(
                                                                            'openInCustomiseMode',
                                                                            'true',
                                                                        );
                                                                        navigate(`/handout/${item.baseContentId}`);
                                                                    }
                                                                }}
                                                                sx={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    background: '#FF669B',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { background: '#FF85B0' },
                                                                }}
                                                            >
                                                                <img src={EditIcon} alt="Edit" width={22} height={22} />
                                                            </Box>
                                                        </Tooltip>
                                                        <Tooltip title="Remove from favourites" arrow>
                                                            <Box
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setArchiveTarget(item);
                                                                    setArchiveTargetType(
                                                                        isCustomisedFavourite
                                                                            ? 'customisedFavourite'
                                                                            : 'favourite',
                                                                    );
                                                                }}
                                                                sx={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    overflow: 'hidden',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { filter: 'brightness(1.12)' },
                                                                }}
                                                            >
                                                                <img
                                                                    src={BinArchiveIcon}
                                                                    alt="Archive"
                                                                    width={50}
                                                                    height={50}
                                                                />
                                                            </Box>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            <Box
                                sx={{
                                    mt: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <IconButton
                                        size="small"
                                        disabled={safeSavedPage <= 1}
                                        onClick={() => setSavedCurrentPage((value) => Math.max(1, value - 1))}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '32px',
                                            border: '1.313px solid #E5E5E5',
                                            background: '#FFFFFF',
                                            color: '#6D6D6D',
                                            '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                        }}
                                    >
                                        <ChevronLeftIcon sx={{ fontSize: '15px' }} />
                                    </IconButton>
                                    {visibleSavedPages.map((pageItem, index) => {
                                        if (pageItem === 'ellipsis') {
                                            return (
                                                <Box
                                                    key={`saved-ellipsis-${index}`}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '8px',
                                                        background: '#FFFFFF',
                                                        color: '#616063',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '16px',
                                                    }}
                                                >
                                                    ...
                                                </Box>
                                            );
                                        }

                                        const isActive = pageItem === safeSavedPage;
                                        return (
                                            <Box
                                                key={`saved-${pageItem}`}
                                                onClick={() => setSavedCurrentPage(pageItem)}
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '32px',
                                                    border: isActive ? 'none' : '1.313px solid #E5E5E5',
                                                    background: isActive ? '#FF669B' : '#FFFFFF',
                                                    color: isActive ? '#FFFFFF' : '#616063',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '13px',
                                                    fontWeight: isActive ? 400 : 500,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {pageItem}
                                            </Box>
                                        );
                                    })}
                                    <IconButton
                                        size="small"
                                        disabled={safeSavedPage >= savedPageCount}
                                        onClick={() =>
                                            setSavedCurrentPage((value) => Math.min(savedPageCount, value + 1))
                                        }
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '32px',
                                            border: '1.313px solid #E5E5E5',
                                            background: '#FFFFFF',
                                            color: '#6D6D6D',
                                            '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                        }}
                                    >
                                        <ChevronRightIcon sx={{ fontSize: '15px' }} />
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Typography
                                        sx={{
                                            color: '#616063',
                                            fontSize: { xs: '14px', md: '13px' },
                                            lineHeight: { xs: '14px', md: '13px' },
                                        }}
                                    >
                                        Items per page
                                    </Typography>
                                    <Select
                                        size="small"
                                        value={savedItemsPerPage}
                                        onChange={(event) => setSavedItemsPerPage(Number(event.target.value))}
                                        sx={{
                                            width: { xs: 63, md: 'auto' },
                                            height: 30,
                                            borderRadius: '10px',
                                            fontSize: '13px',
                                            color: '#616063',
                                            '& .MuiSelect-select': { py: 0, minHeight: 'unset', lineHeight: '13px' },
                                        }}
                                    >
                                        {[4, 8, 12].map((value) => (
                                            <MenuItem key={value} value={value}>
                                                {value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '1361px',
                        minHeight: filteredAndSortedHandouts.length === 0 ? 'auto' : { xs: 'auto', md: '566px' },
                        background: '#FFFFFF',
                        borderRadius: '30px',
                        border: '1px solid #ECECFA',
                        p: { xs: '14px', md: '20px' },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: { xs: 'flex-start', md: 'space-between' },
                            alignItems: { xs: 'flex-start', md: 'center' },
                            mb: '18px',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: { xs: '20px', md: '30px' },
                                lineHeight: { xs: '20px', md: '30px' },
                                fontWeight: 600,
                                color: '#000453',
                                letterSpacing: { xs: '-0.8px', md: '-1.2px' },
                            }}
                        >
                            Customised handouts
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: '6px', md: 0 } }}>
                            <Typography
                                onClick={handleOpenMenu}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: { xs: '12px', md: '14px' },
                                    lineHeight: { xs: '12px', md: '14px' },
                                    letterSpacing: { xs: '1.826px', md: '2px' },
                                    textTransform: 'uppercase',
                                    color: '#1919194D',
                                    cursor: 'pointer',
                                }}
                            >
                                SORT BY{' '}
                                <Box component="span" sx={{ color: '#000453' }}>
                                    {sortBy}
                                </Box>
                            </Typography>
                            <IconButton onClick={handleOpenMenu} sx={{ ml: 0.5, p: 0 }}>
                                {sortBy === 'RECENT' && hasSortedRecently ? (
                                    sortDirection === 'desc' ? (
                                        <ExpandMoreIcon
                                            sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }}
                                        />
                                    ) : (
                                        <ExpandLessIcon
                                            sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }}
                                        />
                                    )
                                ) : (
                                    <ExpandMoreIcon sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }} />
                                )}
                            </IconButton>
                        </Box>
                    </Box>

                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleCloseMenu(null)}>
                        <MenuItem onClick={() => handleCloseMenu('RECENT')} sx={{ color: '#000453' }}>
                            RECENT
                        </MenuItem>
                        <MenuItem onClick={() => handleCloseMenu('FAVOURITES')} sx={{ color: '#000453' }}>
                            FAVOURITES
                        </MenuItem>
                    </Menu>

                    {filteredAndSortedHandouts.length === 0 ? (
                        <Box sx={{ mt: { xs: 3, md: 4 }, mb: { xs: 1, md: 2 }, textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ color: '#000453' }}>
                                {isSearchingHandouts
                                    ? 'No customised handouts match your search.'
                                    : 'No customised handouts available yet.'}
                            </Typography>
                            {/* CETRA-754: Explain the first customised handout workflow only for a genuinely empty library. */}
                            {!isSearchingHandouts && hasNoCustomisedHandouts && (
                                <Typography
                                    sx={{
                                        maxWidth: '760px',
                                        mx: 'auto',
                                        mt: 2,
                                        color: '#000453',
                                        fontSize: { xs: '14px', md: '16px' },
                                        lineHeight: { xs: '20px', md: '24px' },
                                        fontWeight: 400,
                                    }}
                                >
                                    You don&apos;t have any customised handouts yet. To create one, select a handout and
                                    start a &apos;customise&apos; session. Edit the content using the text editing and
                                    whiteboard tools, then click &apos;save&apos; to save your customised handout.
                                </Typography>
                            )}
                            {!isSearchingHandouts && (
                                <Box sx={{ mt: 3, mb: 1, display: 'flex', justifyContent: 'center' }}>
                                    <PinkGradientButtonWithArrow
                                        label="Search handouts"
                                        onClick={() => navigate('/search')}
                                        buttonStyle="figmaCta"
                                    />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2}>
                                {paginatedHandouts.map((item) => {
                                    const title = getDocumentTitle(item);
                                    const previewText = getFirstParagraph(item);
                                    const imageSrc = getImageSource(item);
                                    const isFavourite = userDataStore.userData.customisedHandoutsFavourites?.includes(
                                        item.customisedId,
                                    );

                                    return (
                                        <Grid item xs={12} sm={6} md={3} key={item.customisedId}>
                                            <Box
                                                onClick={() => handleOpenCustomisedHandoutView(item)}
                                                sx={{
                                                    background: '#F0F0FC',
                                                    borderRadius: '20px',
                                                    p: '10px',
                                                    border: '1px solid #ECECFA',
                                                    width: '100%',
                                                    maxWidth: { xs: '100%', md: '314px' },
                                                    mx: { xs: 0, md: 'auto' },
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        background: '#FFFFFF',
                                                        borderRadius: '10px',
                                                        p: '8px',
                                                        minHeight: '248px',
                                                    }}
                                                >
                                                    <FavouriteButton
                                                        isFavourite={isFavourite}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggleFavouriteCustomisedHandout(item.customisedId);
                                                        }}
                                                        size={32}
                                                        iconSize={32}
                                                        tooltipTitle={
                                                            isFavourite ? 'Remove from favourites' : 'Add to favourites'
                                                        }
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '10px',
                                                            right: '10px',
                                                            zIndex: 2,
                                                        }}
                                                    />

                                                    <Typography
                                                        sx={{
                                                            fontSize: '11px',
                                                            lineHeight: '14px',
                                                            color: '#A1A1B3',
                                                            pr: '36px',
                                                            mb: 1,
                                                        }}
                                                    >
                                                        Updated on:{' '}
                                                        {new Date(item.lastUpdated).toLocaleString(location)}
                                                    </Typography>

                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: '168px',
                                                            borderRadius: '10px',
                                                            overflow: 'hidden',
                                                            background: '#FFFFFF',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        {imageSrc ? (
                                                            <img
                                                                src={imageSrc}
                                                                alt={title}
                                                                {...protectedImageProps}
                                                                style={mergeProtectedImageStyle({
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain',
                                                                })}
                                                            />
                                                        ) : (
                                                            <Typography sx={{ color: '#A1A1B3', fontSize: '12px' }}>
                                                                No preview
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    {/* Note snapshot or Add note button inside the image section */}
                                                    <Box
                                                        sx={{
                                                            mt: '8px',
                                                            minHeight: '28px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {item.notes ? (
                                                            <TruncatedTooltip
                                                                text={item.notes}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setNoteTarget(item);
                                                                    setNoteText(item.notes);
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                component="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setNoteTarget(item);
                                                                    setNoteText('');
                                                                }}
                                                                sx={{
                                                                    border: '1.5px solid #FF669B',
                                                                    borderRadius: '999px',
                                                                    px: '12px',
                                                                    py: '4px',
                                                                    color: '#FF669B',
                                                                    fontSize: '13px',
                                                                    fontWeight: 500,
                                                                    background: 'transparent',
                                                                    cursor: 'pointer',
                                                                    fontFamily: 'Inter, sans-serif',
                                                                    '&:hover': { background: 'rgba(255,102,155,0.08)' },
                                                                }}
                                                            >
                                                                Add note
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>

                                                <Typography
                                                    sx={{
                                                        color: '#000453',
                                                        fontWeight: 600,
                                                        fontSize: '22px',
                                                        lineHeight: '24px',
                                                        letterSpacing: '-0.36px',
                                                        mt: '10px',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        minHeight: '48px',
                                                    }}
                                                >
                                                    {title}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: '#616063',
                                                        fontSize: '14px',
                                                        lineHeight: '18px',
                                                        mt: '6px',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        minHeight: '36px',
                                                    }}
                                                >
                                                    {previewText}
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        mt: '10px',
                                                        borderTop: '1px solid #D8D8EA',
                                                        pt: '10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <Tooltip title="Quick share" arrow>
                                                        <Box
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleQuickShare(item);
                                                            }}
                                                            sx={{
                                                                width: '50px',
                                                                height: '50px',
                                                                borderRadius: '50%',
                                                                background: '#FF669B',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                '&:hover': { background: '#FF85B0' },
                                                            }}
                                                        >
                                                            <img
                                                                src={ShareEnvelopIcon}
                                                                alt="Share"
                                                                width={22}
                                                                height={22}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <Tooltip title="Open in customise mode" arrow>
                                                            <Box
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditCustomisedHandout(item);
                                                                }}
                                                                sx={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    background: '#FF669B',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { background: '#FF85B0' },
                                                                }}
                                                            >
                                                                <img src={EditIcon} alt="Edit" width={22} height={22} />
                                                            </Box>
                                                        </Tooltip>
                                                        <Tooltip title="Archive handout" arrow>
                                                            <Box
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setArchiveTarget(item);
                                                                    setArchiveTargetType('customised');
                                                                }}
                                                                sx={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    overflow: 'hidden',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { filter: 'brightness(1.12)' },
                                                                }}
                                                            >
                                                                <img
                                                                    src={BinArchiveIcon}
                                                                    alt="Archive"
                                                                    width={50}
                                                                    height={50}
                                                                />
                                                            </Box>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            <Box
                                sx={{
                                    mt: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <IconButton
                                        size="small"
                                        disabled={safePage <= 1}
                                        onClick={() => setCurrentPage((value) => Math.max(1, value - 1))}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '32px',
                                            border: '1.313px solid #E5E5E5',
                                            background: '#FFFFFF',
                                            color: '#6D6D6D',
                                            '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                        }}
                                    >
                                        <ChevronLeftIcon sx={{ fontSize: '15px' }} />
                                    </IconButton>
                                    {visiblePages.map((pageItem, index) => {
                                        if (pageItem === 'ellipsis') {
                                            return (
                                                <Box
                                                    key={`ellipsis-${index}`}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '8px',
                                                        background: '#FFFFFF',
                                                        color: '#616063',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '16px',
                                                    }}
                                                >
                                                    ...
                                                </Box>
                                            );
                                        }

                                        const isActive = pageItem === safePage;
                                        return (
                                            <Box
                                                key={pageItem}
                                                onClick={() => setCurrentPage(pageItem)}
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '32px',
                                                    border: isActive ? 'none' : '1.313px solid #E5E5E5',
                                                    background: isActive ? '#FF669B' : '#FFFFFF',
                                                    color: isActive ? '#FFFFFF' : '#616063',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '13px',
                                                    fontWeight: isActive ? 400 : 500,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {pageItem}
                                            </Box>
                                        );
                                    })}
                                    <IconButton
                                        size="small"
                                        disabled={safePage >= pageCount}
                                        onClick={() => setCurrentPage((value) => Math.min(pageCount, value + 1))}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '32px',
                                            border: '1.313px solid #E5E5E5',
                                            background: '#FFFFFF',
                                            color: '#6D6D6D',
                                            '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                        }}
                                    >
                                        <ChevronRightIcon sx={{ fontSize: '15px' }} />
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Typography
                                        sx={{
                                            color: '#616063',
                                            fontSize: { xs: '14px', md: '13px' },
                                            lineHeight: { xs: '14px', md: '13px' },
                                        }}
                                    >
                                        Items per page
                                    </Typography>
                                    <Select
                                        size="small"
                                        value={itemsPerPage}
                                        onChange={(event) => setItemsPerPage(Number(event.target.value))}
                                        sx={{
                                            width: { xs: 63, md: 'auto' },
                                            height: 30,
                                            borderRadius: '10px',
                                            fontSize: '13px',
                                            color: '#616063',
                                            '& .MuiSelect-select': { py: 0, minHeight: 'unset', lineHeight: '13px' },
                                        }}
                                    >
                                        {[4, 8, 12].map((value) => (
                                            <MenuItem key={value} value={value}>
                                                {value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            <Dialog open={Boolean(noteTarget)} onClose={() => setNoteTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: '#000453', fontWeight: 700, fontSize: '24px' }}>Add note</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Type a quick note that will be saved with this whiteboard..."
                        sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF669B' },
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setNoteTarget(null)} sx={{ color: '#FF669B', textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveNote}
                        variant="contained"
                        disabled={!noteText.trim()}
                        sx={{
                            background: '#FF669B',
                            '&:hover': { background: '#FF85B0' },
                            borderRadius: '999px',
                            textTransform: 'none',
                            px: 3,
                        }}
                    >
                        Save note
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(archiveTarget)}
                onClose={() => setArchiveTarget(null)}
                PaperProps={{
                    sx: {
                        p: { xs: 2, sm: 4 },
                        minWidth: { xs: 'auto', sm: '500px' },
                        maxWidth: '711px',
                        borderRadius: 4,
                        width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                        m: { xs: 2, sm: 4 },
                    },
                }}
            >
                <DialogTitle
                    variant="h4"
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        p: 0,
                        fontWeight: 600,
                    }}
                >
                    Are you sure you want to{' '}
                    {archiveTargetType === 'favourite' || archiveTargetType === 'customisedFavourite'
                        ? 'remove'
                        : 'archive'}{' '}
                    this{' '}
                    {archiveTargetType === 'favourite' || archiveTargetType === 'customisedFavourite'
                        ? 'favourite handout'
                        : 'customised handout'}
                    ?
                </DialogTitle>
                <DialogContent sx={{ p: 0, mt: 3, mb: 4 }}>
                    <Typography variant="body1" sx={{ color: '#616063' }}>
                        This action will{' '}
                        {archiveTargetType === 'favourite' || archiveTargetType === 'customisedFavourite'
                            ? 'remove'
                            : 'archive'}{' '}
                        the{' '}
                        {archiveTargetType === 'favourite' || archiveTargetType === 'customisedFavourite'
                            ? 'favourite handout'
                            : 'customised handout'}{' '}
                        and remove it from your list.
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        p: 0,
                        mt: { xs: 0, sm: 3 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                    }}
                >
                    <PinkGradientButton
                        onClick={
                            archiveTargetType === 'favourite' || archiveTargetType === 'customisedFavourite'
                                ? handleRemoveFavourite
                                : handleArchiveCustomisedHandout
                        }
                        sx={{
                            my: { xs: 1, sm: 1.5 },
                            mr: { sm: 2 },
                            background: '#FF669B',
                            backgroundImage: 'none',
                            '&:hover': { background: '#FF85B0', backgroundImage: 'none' },
                        }}
                    >
                        Yes
                    </PinkGradientButton>
                    <Button
                        onClick={() => setArchiveTarget(null)}
                        sx={{
                            color: '#0b0c26',
                            textDecoration: 'underline',
                            textTransform: 'none',
                            fontWeight: 600,
                            height: { xs: 'auto', sm: 48 },
                            mt: { xs: 1, sm: 0 },
                        }}
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>

            {quickShareHandout && (
                <QuickShareDialog
                    open={quickShareOpen}
                    handout={quickShareHandout}
                    onClose={() => {
                        setQuickShareOpen(false);
                        setQuickShareHandout(null);
                    }}
                />
            )}
        </Box>
    );
});

export default MyLibrary;
